import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Observable, catchError, finalize, shareReplay, switchMap, throwError } from 'rxjs';

import { AuthService } from './auth.service';

const AUTH_ROUTE_WHITELIST = ['api/auth/login', 'api/auth/refreshtoken'];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private _authService = inject(AuthService);
  private _matSnackbarService = inject(MatSnackBar);

  private refreshToken$?: Observable<unknown>;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const authReq = this._attachAccessToken(req);

    return next.handle(authReq).pipe(
      catchError((error) => {
        if (this._shouldHandle401(error, authReq)) {
          return this._handle401(authReq, next);
        }

        return throwError(() => error);
      }),
    );
  }

  //#region 401 Handling
  private _handle401(request: HttpRequest<unknown>, next: HttpHandler) {
    if (!this.refreshToken$) {
      this.refreshToken$ = this._createRefreshRequest();
    }

    return this.refreshToken$.pipe(
      switchMap(() => {
        return next.handle(this._attachAccessToken(request));
      }),
    );
  }

  private _createRefreshRequest() {
    const refreshToken = this._authService.getRefreshToken();

    if (!refreshToken) {
      return this._handleRefreshFailure(
        new HttpErrorResponse({
          status: 401,
          error: 'No refresh token found',
        }),
      );
    }

    return this._authService.refreshToken(refreshToken).pipe(
      catchError((error) => this._handleRefreshFailure(error)),
      finalize(() => (this.refreshToken$ = undefined)),
      shareReplay(1),
    );
  }
  //#endregion 401 Handling

  //#region Helpers
  private _attachAccessToken(request: HttpRequest<unknown>) {
    const accessToken = this._authService.getToken();

    if (!accessToken) {
      return request;
    }

    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${accessToken}`),
    });
  }

  private _shouldHandle401(error: Error, request: HttpRequest<unknown>): boolean {
    return error instanceof HttpErrorResponse && error.status === 401 && !AUTH_ROUTE_WHITELIST.includes(request.url);
  }

  private _handleRefreshFailure(error: Error) {
    if (error instanceof HttpErrorResponse && error.status === 401) {
      this._matSnackbarService.open('Login session expired, please log in again.');
      this._authService.logout();
    }

    return throwError(() => error);
  }
  //#endregion Helpers
}
